<script lang="ts">
  // The members explorer filter bar: a search field + the two population toggles
  // (moderators, ex-members), then the categorical facets (Membership, Has role,
  // Had role, Engagement, Style) as multi-select pills. Pills fill with ink when
  // selected; the semantic facets carry a colour dot that matches the
  // role/engagement/membership hues used on avatar rings and badges across the
  // panel, so the filter reads as part of the same system. "Has role" is the
  // member's CURRENT contributor category; "Had role" is any category they have
  // ever held — combine them to find transitions (e.g. was a key voice, now a
  // broadcaster). "Membership" mirrors Skool's own members-page tabs, with its
  // churned tab already split into churned/left/removed by the roster sweep.
  // "Has role" also carries the terminal `departed` role — the contributor-side way
  // to the same people the Membership chips and the ex-members toggle reach.
  // Owns presentation only — every value is a $bindable the page holds, and any
  // change calls `onchange` so the page reloads + mirrors to the URL.
  import { MEMBER_STATUS_META, MEMBER_STATUS_ORDER } from '../admin';
  import { QUAD, ROLE_ORDER, ROLE_ORDER_ALL } from '@ui/quadrant';
  import { STAGE_META, STAGE_ORDER } from '@ui/stages';

  let {
    q = $bindable(''),
    roles = $bindable<string[]>([]),
    hadRoles = $bindable<string[]>([]),
    stages = $bindable<string[]>([]),
    styles = $bindable<string[]>([]),
    statuses = $bindable<string[]>([]),
    includeModerators = $bindable(false),
    cohort = '',
    retained = 0,
    onClearCohort,
    onchange,
  }: {
    q?: string;
    roles?: string[];
    hadRoles?: string[];
    stages?: string[];
    styles?: string[];
    /** Membership states to keep (MemberStatus values); empty = everyone who has
     *  ever been a member, ex-members included. */
    statuses?: string[];
    includeModerators?: boolean;
    /** Active first-contribution cohort deep-link (YYYY-MM), shown as a chip. */
    cohort?: string;
    retained?: number;
    onClearCohort?: () => void;
    onchange: () => void;
  } = $props();

  type Opt = { value: string; label: string; color?: string };
  const STATUS_OPTS: Opt[] = MEMBER_STATUS_ORDER.map((s) => ({
    value: s,
    label: MEMBER_STATUS_META[s].label,
    color: MEMBER_STATUS_META[s].color,
  }));
  const toOpt = (r: (typeof ROLE_ORDER_ALL)[number]): Opt => ({ value: r, label: QUAD[r].label, color: QUAD[r].color });
  // "Has role" carries the terminal `departed` chip: it's the member's current
  // contributor category, and for someone who left that IS departed. Picking it
  // widens the population server-side, like the Membership chips, so it can't come
  // back empty.
  const ROLE_OPTS: Opt[] = ROLE_ORDER_ALL.map(toOpt);
  // "Had role" is the live roles only. Departure is terminal, so "ever departed" and
  // "is departed" are the same set of people — offering it here would just be the
  // chip above under a second name.
  const HAD_ROLE_OPTS: Opt[] = ROLE_ORDER.map(toOpt);
  // Momentum is live stages only, and has no departed chip: "who left?" is asked on
  // the role facet above (or the Membership status chips), because departure lives on
  // the contributor-role axis, not this one. See stages.ts.
  const STAGE_OPTS: Opt[] = STAGE_ORDER.map((s) => ({ value: s, label: STAGE_META[s].label, color: STAGE_META[s].color }));
  const STYLE_OPTS: Opt[] = [
    { value: 'poster', label: 'Poster' },
    { value: 'commenter', label: 'Commenter' },
    { value: 'poster_commenter', label: 'Poster & commenter' },
  ];

  let searchTimer: ReturnType<typeof setTimeout> | undefined;

  const activeCount = $derived(
    (q.trim() ? 1 : 0) +
      roles.length +
      hadRoles.length +
      stages.length +
      styles.length +
      statuses.length +
      (includeModerators ? 1 : 0),
  );

  function onSearch(e: Event): void {
    q = (e.target as HTMLInputElement).value;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(onchange, 300);
  }
  function toggleIn(list: string[], v: string): string[] {
    return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
  }
  function toggleRole(v: string): void {
    roles = toggleIn(roles, v);
    onchange();
  }
  function toggleHadRole(v: string): void {
    hadRoles = toggleIn(hadRoles, v);
    onchange();
  }
  function toggleStage(v: string): void {
    stages = toggleIn(stages, v);
    onchange();
  }
  function toggleStyle(v: string): void {
    styles = toggleIn(styles, v);
    onchange();
  }
  function toggleStatus(v: string): void {
    statuses = toggleIn(statuses, v);
    onchange();
  }
  function toggleMods(e: Event): void {
    includeModerators = (e.target as HTMLInputElement).checked;
    onchange();
  }
  function clearAll(): void {
    q = '';
    roles = [];
    hadRoles = [];
    stages = [];
    styles = [];
    statuses = [];
    includeModerators = false;
    if (cohort) onClearCohort?.();
    else onchange();
  }
  function cohortLabel(m: string): string {
    const [y, mo] = m.split('-');
    return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }
</script>

{#snippet facet(name: string, opts: Opt[], selected: string[], toggle: (v: string) => void)}
  <div class="flex items-start gap-3">
    <!-- privacy-ok: the filter ROW's label ("MEMBERSHIP", "HAD ROLE"), not a person -->
    <span class="eyebrow mt-1.5 w-24 shrink-0">{name}</span>
    <div class="flex flex-wrap gap-1.5">
      {#each opts as o (o.value)}
        {@const on = selected.includes(o.value)}
        <button
          type="button"
          class={'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ' +
            (on ? 'bg-primary text-primary-content' : 'bg-base-200/70 text-base-content/70 hover:bg-base-200 hover:text-base-content')}
          aria-pressed={on}
          onclick={() => toggle(o.value)}
        >
          {#if o.color}
            <span class="h-2 w-2 shrink-0 rounded-full" style="background:{o.color}"></span>
          {/if}
          {o.label}
        </button>
      {/each}
    </div>
  </div>
{/snippet}

<div class="rounded-2xl border border-base-300 bg-base-100 p-3 shadow-sm sm:p-4">
  <!-- search + moderators -->
  <div class="flex flex-wrap items-center gap-3">
    <label class="flex h-9 min-w-[14rem] flex-1 items-center gap-2 rounded-xl border border-base-300 bg-base-100 px-3 focus-within:border-primary">
      <svg class="h-4 w-4 shrink-0 opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
      <input
        type="search"
        placeholder="Search name or @handle…"
        class="w-full bg-transparent text-sm outline-none placeholder:text-base-content/62"
        value={q}
        oninput={onSearch}
        aria-label="Search members"
      />
    </label>
    <label class="flex cursor-pointer select-none items-center gap-2 text-xs text-base-content/72">
      <input type="checkbox" class="toggle toggle-sm" checked={includeModerators} onchange={toggleMods} />
      Include moderators
    </label>
  </div>

  <!-- facets -->
  <div class="mt-3 space-y-2.5">
    {@render facet('Membership', STATUS_OPTS, statuses, toggleStatus)}
    {@render facet('Had role', HAD_ROLE_OPTS, hadRoles, toggleHadRole)}
    {@render facet('Has role', ROLE_OPTS, roles, toggleRole)}
    {@render facet('Engagement', STAGE_OPTS, stages, toggleStage)}
    {@render facet('Style', STYLE_OPTS, styles, toggleStyle)}
  </div>

  <!-- footer: active summary + cohort chip + clear -->
  {#if activeCount > 0 || cohort}
    <div class="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-base-200 pt-2.5 text-xs">
      <span class="text-base-content/68">
        {#if activeCount > 0}{activeCount} filter{activeCount === 1 ? '' : 's'} active{:else}Filtered by cohort{/if}
      </span>
      <div class="flex items-center gap-2">
        {#if cohort}
          <button
            class="inline-flex items-center gap-1.5 rounded-full border border-success/40 bg-success/10 px-2.5 py-1 font-mono text-success transition-colors hover:bg-success/20"
            onclick={() => onClearCohort?.()}
            title="Clear cohort filter"
          >
            {cohortLabel(cohort)} cohort{retained ? ` · stayed ${retained}d+` : ''}
            <span aria-hidden="true" class="text-success/70">✕</span>
          </button>
        {/if}
        {#if activeCount > 0 || cohort}
          <button class="font-medium text-base-content/70 hover:text-base-content" onclick={clearAll}>Clear all</button>
        {/if}
      </div>
    </div>
  {/if}
</div>
